import {omneticApi} from '@dms/api/base';

import {
  CancelRentalOrderVehicleApiResponse,
  CancelRentalOrderVehicleApiArg,
  ChangeRentalOrderCustomerApiResponse,
  ChangeRentalOrderCustomerApiArg,
  ChangeOrderRentVehicleApiResponse,
  ChangeOrderRentVehicleApiArg,
  ChangeOrderReturnVehicleApiResponse,
  ChangeOrderReturnVehicleApiArg,
  ChangeRentalOrderServiceOrderApiResponse,
  ChangeRentalOrderServiceOrderApiArg,
  ChangeRentalOrderVehicleApiResponse,
  ChangeRentalOrderVehicleApiArg,
  CreateRentalOrderApiResponse,
  CreateRentalOrderApiArg,
  CreateRentalOrderContractDocumentApiResponse,
  CreateRentalOrderContractDocumentApiArg,
  CreateRentalOrderReturnDocumentApiResponse,
  CreateRentalOrderReturnDocumentApiArg,
  GetRentalOrderApiResponse,
  GetRentalOrderApiArg,
  RemoveRentalOrderApiResponse,
  RemoveRentalOrderApiArg,
  RentRentalOrderVehicleApiResponse,
  RentRentalOrderVehicleApiArg,
  ReserveRentalOrderVehicleApiResponse,
  ReserveRentalOrderVehicleApiArg,
  ReturnRentalOrderVehicleApiResponse,
  ReturnRentalOrderVehicleApiArg,
  CreateRentalSettingsApiResponse,
  CreateRentalSettingsApiArg,
  ListRentalSettingsApiResponse,
  ListRentalSettingsApiArg,
  GetRentalSettingsApiResponse,
  GetRentalSettingsApiArg,
  RemoveRentalSettingsApiResponse,
  RemoveRentalSettingsApiArg,
  UpdateRentalSettingsApiResponse,
  UpdateRentalSettingsApiArg,
  AvailableVehicleListApiResponse,
  AvailableVehicleListApiArg,
  ChangeRentalOrderBranchApiArg,
  ChangeRentalOrderBranchApiResponse,
  RentingStateApiArg,
  RentingStateApiResponse,
  ChangeRentVehicleInspectionApiResponse,
  ChangeRentVehicleInspectionApiArg,
  ChangeReturnVehicleInspectionApiArg,
  ChangeReturnVehicleInspectionApiResponse,
  RemoveRentalOrderServiceOrderApiResponse,
  RemoveRentalOrderServiceOrderApiArg,
} from './types';

const rentalOrderApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    cancelRentalOrderVehicle: build.mutation<
      CancelRentalOrderVehicleApiResponse,
      CancelRentalOrderVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}/cancel`,
        method: 'POST',
      }),
      invalidatesTags: (res, err, queryArg) => [{type: 'RentalOrder', id: queryArg.rentalOrderId}],
    }),
    changeRentalOrderCustomer: build.mutation<
      ChangeRentalOrderCustomerApiResponse,
      ChangeRentalOrderCustomerApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}/customer`,
        method: 'PUT',
        body: queryArg.changeRentalOrderCustomerRequestBody,
      }),
      invalidatesTags: (res, err, queryArg) => [{type: 'RentalOrder', id: queryArg.rentalOrderId}],
    }),
    changeOrderRentVehicle: build.mutation<
      ChangeOrderRentVehicleApiResponse,
      ChangeOrderRentVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}/rent-vehicle`,
        method: 'PUT',
        body: queryArg.changeOrderRentVehicleRequestBody,
      }),
      invalidatesTags: (res, err, queryArg) => [{type: 'RentalOrder', id: queryArg.rentalOrderId}],
    }),
    changeOrderReturnVehicle: build.mutation<
      ChangeOrderReturnVehicleApiResponse,
      ChangeOrderReturnVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}/return-vehicle`,
        method: 'PUT',
        body: queryArg.changeOrderReturnVehicleRequestBody,
      }),
      invalidatesTags: (res, err, queryArg) => [{type: 'RentalOrder', id: queryArg.rentalOrderId}],
    }),
    changeRentalOrderServiceOrder: build.mutation<
      ChangeRentalOrderServiceOrderApiResponse,
      ChangeRentalOrderServiceOrderApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}/service-order`,
        method: 'PUT',
        body: queryArg.changeRentalOrderServiceOrderRequestBody,
      }),
    }),
    removeRentalOrderServiceOrder: build.mutation<
      RemoveRentalOrderServiceOrderApiResponse,
      RemoveRentalOrderServiceOrderApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}/service-order`,
        method: 'DELETE',
      }),
    }),
    changeRentalOrderVehicle: build.mutation<
      ChangeRentalOrderVehicleApiResponse,
      ChangeRentalOrderVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}/vehicle`,
        method: 'PUT',
        body: queryArg.changeRentalOrderVehicleRequestBody,
      }),
      invalidatesTags: (res, err, queryArg) => [{type: 'RentalOrder', id: queryArg.rentalOrderId}],
    }),
    rentingState: build.query<RentingStateApiResponse, RentingStateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}/renting-state`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'RentalOrder', id: queryArg.rentalOrderId},
      ],
    }),
    createRentalOrder: build.mutation<CreateRentalOrderApiResponse, CreateRentalOrderApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order`,
        method: 'POST',
        body: queryArg.createRentalOrderRequestBody,
      }),
      invalidatesTags: (response) => [{type: 'RentalOrder', id: response?.id}],
    }),
    createRentalOrderContractDocument: build.mutation<
      CreateRentalOrderContractDocumentApiResponse,
      CreateRentalOrderContractDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}/contract-document`,
        method: 'POST',
        body: {templateId: queryArg.templateId},
      }),
    }),
    createRentalOrderReturnDocument: build.mutation<
      CreateRentalOrderReturnDocumentApiResponse,
      CreateRentalOrderReturnDocumentApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}/return-document`,
        method: 'POST',
        body: {templateId: queryArg.templateId},
      }),
    }),
    getRentalOrder: build.query<GetRentalOrderApiResponse, GetRentalOrderApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'RentalOrder', id: queryArg.rentalOrderId},
      ],
    }),
    removeRentalOrder: build.mutation<RemoveRentalOrderApiResponse, RemoveRentalOrderApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}`,
        method: 'DELETE',
      }),
    }),
    rentRentalOrderVehicle: build.mutation<
      RentRentalOrderVehicleApiResponse,
      RentRentalOrderVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}/rent`,
        method: 'POST',
      }),
      invalidatesTags: (res, err, queryArg) => [{type: 'RentalOrder', id: queryArg.rentalOrderId}],
    }),
    reserveRentalOrderVehicle: build.mutation<
      ReserveRentalOrderVehicleApiResponse,
      ReserveRentalOrderVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}/reserve`,
        method: 'POST',
      }),
      invalidatesTags: (res, err, queryArg) => [{type: 'RentalOrder', id: queryArg.rentalOrderId}],
    }),
    returnRentalOrderVehicle: build.mutation<
      ReturnRentalOrderVehicleApiResponse,
      ReturnRentalOrderVehicleApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}/return`,
        method: 'POST',
      }),
      invalidatesTags: (res, err, queryArg) => [{type: 'RentalOrder', id: queryArg.rentalOrderId}],
    }),
    createRentalSettings: build.mutation<
      CreateRentalSettingsApiResponse,
      CreateRentalSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-settings`,
        method: 'POST',
        body: queryArg.createRentalSettingsRequestBody,
      }),
      invalidatesTags: ['RentalSettings'],
    }),
    listRentalSettings: build.query<ListRentalSettingsApiResponse, ListRentalSettingsApiArg>({
      query: () => ({
        url: `/dms/v1/rental-settings`,
      }),
      providesTags: ['RentalSettings'],
    }),
    getRentalSettings: build.query<GetRentalSettingsApiResponse, GetRentalSettingsApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/rental-settings/${queryArg.rentalSettingsId}`,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'RentalSettings', id: queryArg.rentalSettingsId},
      ],
    }),
    removeRentalSettings: build.mutation<
      RemoveRentalSettingsApiResponse,
      RemoveRentalSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-settings/${queryArg.rentalSettingsId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (res, err, queryArg) => [{type: 'RentalSettings'}],
    }),
    updateRentalSettings: build.mutation<
      UpdateRentalSettingsApiResponse,
      UpdateRentalSettingsApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-settings/${queryArg.rentalSettingsId}`,
        method: 'PUT',
        body: queryArg.updateRentalSettingsRequestBody,
      }),
      invalidatesTags: (res, err, queryArg) => [
        {type: 'RentalSettings', id: queryArg.rentalSettingsId},
        {type: 'RentalSettings'},
      ],
    }),
    changeRentalOrderBranch: build.mutation<
      ChangeRentalOrderBranchApiResponse,
      ChangeRentalOrderBranchApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}/branch`,
        method: 'PUT',
        body: queryArg.changeRentalOrderBranchRequestBody,
      }),
      invalidatesTags: (res, err, queryArg) => [{type: 'RentalOrder', id: queryArg.rentalOrderId}],
    }),
    changeRentVehicleInspection: build.mutation<
      ChangeRentVehicleInspectionApiResponse,
      ChangeRentVehicleInspectionApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}/rent-vehicle-inspection`,
        method: 'PUT',
        body: queryArg.changeRentVehicleInspectionRequestBody,
      }),
      invalidatesTags: (res, err, queryArg) => [{type: 'RentalOrder', id: queryArg.rentalOrderId}],
    }),
    changeReturnVehicleInspection: build.mutation<
      ChangeReturnVehicleInspectionApiResponse,
      ChangeReturnVehicleInspectionApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/rental-order/${queryArg.rentalOrderId}/return-vehicle-inspection`,
        method: 'PUT',
        body: queryArg.changeReturnVehicleInspectionRequestBody,
      }),
      invalidatesTags: (res, err, queryArg) => [{type: 'RentalOrder', id: queryArg.rentalOrderId}],
    }),
    availableVehicleList: build.query<AvailableVehicleListApiResponse, AvailableVehicleListApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/available-rental-vehicle`,
        params: {
          from: queryArg.from,
          to: queryArg.to,
          term: queryArg.term,
          rentalOrderId: queryArg.rentalOrderId,
        },
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useChangeReturnVehicleInspectionMutation,
  useChangeRentVehicleInspectionMutation,
  useRentingStateQuery,
  useCancelRentalOrderVehicleMutation,
  useChangeRentalOrderCustomerMutation,
  useChangeOrderRentVehicleMutation,
  useChangeOrderReturnVehicleMutation,
  useChangeRentalOrderServiceOrderMutation,
  useRemoveRentalOrderServiceOrderMutation,
  useChangeRentalOrderVehicleMutation,
  useCreateRentalOrderMutation,
  useCreateRentalOrderContractDocumentMutation,
  useCreateRentalOrderReturnDocumentMutation,
  useGetRentalOrderQuery,
  useRemoveRentalOrderMutation,
  useRentRentalOrderVehicleMutation,
  useReserveRentalOrderVehicleMutation,
  useReturnRentalOrderVehicleMutation,
  useCreateRentalSettingsMutation,
  useListRentalSettingsQuery,
  useGetRentalSettingsQuery,
  useRemoveRentalSettingsMutation,
  useUpdateRentalSettingsMutation,
  useChangeRentalOrderBranchMutation,
  useAvailableVehicleListQuery,
} = rentalOrderApi;
